/**
 * Every respondent-facing literal the form engine emits, in one place.
 *
 * No translation happens yet (decision 4 of the usability playbook): these are
 * English. But a form's respondents are not its builders, and the day a form
 * needs to speak another language the change is a lookup here, not a hunt
 * through the renderer. Anything a respondent can read — labels the engine
 * invents, the validation messages, the pager's buttons, the thank-you title —
 * comes from this module. Builder-side text does not belong here.
 */

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const STRINGS = {
  // ---- controls -----------------------------------------------------------
  submit: 'Submit',
  submitAnother: 'Submit Another',
  next: 'Next →',
  back: '← Back',
  step: (position: number, total: number) => `Step ${position} of ${total}`,
  select: 'Select…',
  optional: ' (optional)',
  /** the word after a required label when the form marks them that way (settings.requiredStyle) */
  requiredLabel: 'Required',
  /** the write-in choice a radio / select / multiselect can offer, and its box */
  other: 'Other',
  otherPrompt: 'Please specify',
  /** the placeholder a US-formatted phone field shows when the builder set none */
  phonePlaceholder: '(555) 555-5555',
  /** the character counter beside the help text: "120 / 500", or "12 (at least 20)" for a floor alone */
  count: (n: number, max: number) => `${n} / ${max}`,
  countMin: (n: number, min: number) => `${n} (at least ${min})`,
  remove: (item: string) => `Remove ${item}`,
  add: (item: string) => `+ Add ${item}`,

  // ---- thank-you ----------------------------------------------------------
  thankYou: 'Thank You!',
  received: (submissionId: string) => `Your submission has been received (${submissionId}).`,
  /** the button into the next form, when an ending continues and names no words of its own */
  continueButton: 'Continue to the next form →',
  /** said while an ending that goes straight on opens the next form */
  continuing: 'Taking you to the next form…',

  // ---- edit after submit (P6) ---------------------------------------------
  edit: {
    /** the link on the thank-you page and the heading of the edit page's banner */
    link: 'Edit your response',
    banner: 'You are editing the answers you already sent.',
    until: (when: string) => `You can make changes until ${when}.`,
    submit: 'Save changes',
    savedTitle: 'Changes saved',
    saved: (submissionId: string) => `Your updated answers have been saved (${submissionId}).`,
    nothingChanged: 'Nothing was changed.',
    editAgain: 'Edit again',
    expired: 'This edit link has expired or is no longer valid.',
    /** under a file field that already has an answer */
    provided: (names: string) => `Already provided: ${names}. Add files to replace them.`,
  },

  // ---- save and continue later (P6) ----------------------------------------
  resume: {
    link: 'Save and continue later',
    title: 'Save and continue later',
    body: 'We will email you a link that brings your answers back on any device.',
    email: 'Your email address',
    send: 'Email me the link',
    cancel: 'Cancel',
    sent: (email: string) => `Sent. Check ${email} for the link — it works for 7 days, or until you submit.`,
    invalid: 'Please enter a valid email address.',
    nothingYet: 'Answer something first, then save it for later.',
  },

  // ---- composite sub-labels ----------------------------------------------
  sub: {
    firstName: 'First Name',
    lastName: 'Last Name',
    company: 'Company / Name',
    attention: 'Attention',
    phone: 'Phone Number',
    addressLine1: 'Address Line 1',
    addressLine2: 'Address Line 2',
    country: 'Country',
  },

  // ---- files --------------------------------------------------------------
  filesSelected: (n: number) => `${n} ${plural(n, 'file', 'files')} selected`,
  filesHint: 'You can select multiple files to upload.',
  files: {
    /** the drop zone's invitation; `browse` is the part rendered as the link */
    drop: 'Drag files here or ',
    browse: 'browse',
    dropOne: 'Drag a file here or ',
    /** a finger cannot drag a file onto anything, so touch gets buttons (M2/C3) */
    tap: 'Tap to choose a file',
    tapMany: 'Tap to choose files',
    takePhoto: '📷 Take photo',
    choose: 'Choose file',
    chooseMany: 'Choose files',
    /** "Up to 5 files, 10 MB each · pdf, png, jpg" */
    limits: (maxFiles: number, maxSizeMb: number, types: string) =>
      `${maxFiles === 1 ? 'One file' : `Up to ${maxFiles} files`}, ${maxSizeMb} MB ${maxFiles === 1 ? 'max' : 'each'}${types ? ` · ${types}` : ''}`,
    anyType: 'any common document or image',
    remove: (name: string) => `Remove ${name}`,
    // The refusals, worded exactly as the server words them (lib/uploadRules.ts).
    tooLarge: (mb: number) => `Files must be under ${mb} MB.`,
    tooMany: (n: number) => `You can attach up to ${n} ${plural(n, 'file', 'files')}.`,
    onlyTypes: (list: string[]) => `Only ${list.join(', ')} files are accepted.`,
    notAccepted: 'That file type is not accepted.',
    // The overlay while it all goes up.
    submitting: 'Submitting…',
    uploading: (index: number, count: number, pct: number) => `Uploading ${index} of ${count} · ${pct}%`,
  },

  // ---- payment ------------------------------------------------------------
  payment: 'Payment',
  paymentAfterSubmit: 'You will be taken to a secure Stripe page to pay after you submit.',
  paymentNothing: 'Nothing to pay for this submission.',

  // ---- signature ----------------------------------------------------------
  signature: {
    terms: 'Terms you are agreeing to',
    howToSign: 'How to sign',
    draw: 'Draw',
    type: 'Type',
    typedPlaceholder: 'Type your full name',
    typedLabel: 'Your full name',
    signHere: 'Sign here',
    appearsHere: 'Your signature appears here',
    /** under the pad on a phone, where sideways is twice the room (M2/C3) */
    turnSideways: 'Turn your phone sideways for a bigger pad.',
    clear: 'Clear',
    signAgain: 'Sign again',
    signed: (label: string) => `${label}: signed`,
    pad: (label: string, canType: boolean) =>
      `${label}: signature pad${canType ? ' — or type your name instead' : ''}`,
    signedImage: (label: string) => `${label} (signed)`,
  },

  // ---- notes under a field (why it is already filled) ---------------------
  note: {
    computed: (how: string) => `⚡ computed: ${how}`,
    carriedLocked: '⚡ from your order — contact us if this is wrong',
    carriedConfirm: '⚡ from your order — please check it',
    carriedEditable: '⚡ from your order — you can edit it',
    fromAccount: (provider: string) => `⚡ from your ${provider} account`,
    signedIn: 'signed-in',
    mirrored: (label: string) => `⚡ mirrored from "${label}"`,
    setByForm: '⚡ set by this form — contact us if this needs to change',
    locked: '⚡ locked — contact us if this needs to change',
    prefilled: '⚡ prefilled — you can edit it',
    partsMirrored: '⚡ some parts are mirrored from other answers',
    partsPrefilled: '⚡ some parts are prefilled — you can edit them',
    fromLink: '⚡ set by the link you followed',
    partsFromLink: '⚡ some parts are set by the link you followed',
  },

  // ---- carried-answer confirmation --------------------------------------
  confirm: {
    ask: 'Is this correct?',
    group: (label: string) => `Is the ${label} correct?`,
    yes: 'Yes',
    no: 'No — I have corrected it above',
    missing: 'Please answer this.',
  },

  // ---- captcha and repeat responses (U6) ----------------------------------
  captcha: {
    label: 'Security check',
    required: 'Please complete the security check before submitting.',
    unavailable: 'The security check could not be loaded. Please reload the page and try again.',
  },
  duplicate: {
    title: "You've already responded",
    /** the same default the server uses when the owner wrote nothing */
    body: "It looks like you've already responded.",
  },

  // ---- validation ---------------------------------------------------------
  /** the field-level text under an empty required control */
  required: 'This field is required.',
  /** the summary's heading, with how many things need attention */
  errorHeading: (n: number) => (n === 1 ? 'One thing needs your attention' : `${n} things need your attention`),
  /** the opt-in summary at the top of the page (D15) */
  errorHeadingTop: (n: number) => (n === 1 ? '1 thing to fix' : `${n} things to fix`),
  /** the legacy one-line forms, still produced by formatValidationErrors */
  fillIn: 'Please fill in:',
  check: 'Please check:',
  /** the per-type defaults a field's own `validation.message` overrides */
  v: {
    number: 'must be a number',
    min: (n: number) => `must be at least ${n}`,
    max: (n: number) => `must be at most ${n}`,
    email: 'must be a valid email address',
    url: 'must be a link starting with http:// or https://',
    phone: 'must be a 10-digit phone number',
    minLen: (n: number) => `must be at least ${n} characters`,
    maxLen: (n: number) => `must be at most ${n} characters`,
    pattern: 'does not match the required format',
    dateMin: (bound: string) => `must be on or after ${bound}`,
    dateMax: (bound: string) => `must be on or before ${bound}`,
    // P3 question types — the same words the server answers with
    scale: (min: number, max: number) => `must be between ${min} and ${max}`,
    matrixRows: 'needs an answer on every row',
    choice: 'is not one of the offered choices',
    rankingAll: 'needs every item put in order',
    rankingDup: 'ranks the same item twice',
    datetime: 'must be a date and time',
  },
  // ---- ranking (P3) -------------------------------------------------------
  rank: {
    hint: 'Use the arrows (or drag) to put these in order — 1 is your top choice.',
    /** the same, where there is no pointer to drag with (M2/C3) */
    hintTap: 'Use the arrows to put these in order — 1 is your top choice.',
    keep: 'Keep this order',
    up: (item: string) => `Move ${item} up`,
    down: (item: string) => `Move ${item} down`,
  },
} as const;

export type Strings = typeof STRINGS;
